import { evaluateCondition, type ConditionExpression } from '../engine/condition-evaluator';
import type { CanonicalEdge } from '../models/canonical.types';
import type { CategoryExecutor } from './executor.types';

function isCorporateApplicant(payload: Record<string, unknown>): boolean | undefined {
  for (const field of ['column13', 'applicantType', 'applicantTypes']) {
    const text = String(payload[field] ?? '').toLowerCase();
    if (text === 'corporate') return true;
    if (text === 'individual') return false;
  }
  return undefined;
}

/** Resolve if-else branches when designer edges use true/false handles. */
function resolveIfElseBranch(
  outgoing: CanonicalEdge[],
  payload: Record<string, unknown>,
): CanonicalEdge | undefined {
  const conditional = outgoing.filter(
    (edge) => !edge.isDefault && edge.condition != null,
  );
  for (const edge of conditional) {
    if (evaluateCondition(edge.condition as ConditionExpression, payload)) {
      return edge;
    }
  }

  const defaultEdge = outgoing.find((edge) => edge.isDefault);
  if (defaultEdge) {
    return defaultEdge;
  }

  const trueEdge = outgoing.find((edge) => edge.sourceHandle === 'true');
  const falseEdge = outgoing.find((edge) => edge.sourceHandle === 'false');
  if (trueEdge && falseEdge && conditional.length === 0) {
    const corporate = isCorporateApplicant(payload);
    if (corporate === true) return trueEdge;
    if (corporate === false) return falseEdge;
  }

  return undefined;
}

export const executeFlowControlNode: CategoryExecutor = (node, context, outgoingEdges) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};

  if (
    type.endsWith('.end') ||
    type.endsWith('.terminate') ||
    type.endsWith('.success') ||
    type.endsWith('.failure')
  ) {
    return { status: 'success' };
  }

  if (type.endsWith('.go-to')) {
    const target = typeof config['targetStepId'] === 'string' ? config['targetStepId'] : undefined;
    return {
      status: 'success',
      output: target ? { __gotoNodeKey: target } : {},
    };
  }

  if (type.endsWith('.delay') || type.endsWith('.wait')) {
    // Client-side: no real timer persistence; treat as passthrough.
    return { status: 'success' };
  }

  if (type.endsWith('.manual-task') || type.includes('choice-node')) {
    return { status: 'pause' };
  }

  if (type.endsWith('.retry')) {
    const attempts = Number(context.payload['__retryAttempts'] ?? 0);
    const max = typeof config['maxAttempts'] === 'number' ? config['maxAttempts'] : 3;
    return {
      status: 'success',
      output: { __retryAttempts: attempts + 1, __retryExhausted: attempts + 1 >= max },
    };
  }

  if (type.includes('if-else') || type.includes('ifelse')) {
    const edge = resolveIfElseBranch(outgoingEdges, context.payload);
    if (edge) {
      return {
        status: 'success',
        nextEdgeId: edge.id,
        nextSourceHandle: edge.sourceHandle,
      };
    }
  }

  // start / parallel / merge / loop / continue / sub-workflow — passthrough; routing is edge-driven
  return { status: 'success' };
};
